// =============================================================================
// app/src/lib/codigoBarras.ts — o código de barras da conta a pagar.
//
// Pedido do dono (18/09/2026): "clicar no centro da célula da conta e abrir o
// código de barras embaixo para copiar e enviar ao meu pai" — quem paga no
// banco é o Serginho, e hoje o código viaja por foto do papel.
//
// O QUE SE GUARDA É A LINHA DIGITÁVEL, sempre: 47 números no boleto de banco,
// 48 na conta de convênio (luz, água, telefone, imposto — começa com 8). É o
// que o aplicativo do banco pede em "digitar código". Quem chega com os 44
// números do CÓDIGO DE BARRAS (leitor de código na loja, ou copiado de um PDF)
// tem a linha montada aqui, com os dígitos verificadores de cada campo.
//
// A CONFERÊNCIA é a mesma que o banco faz: dígito de cada campo (módulo 10, ou
// 11 no convênio de identificador 8/9) e o dígito geral. Um número trocado na
// digitação para aqui, na loja, e não no celular do pai com o boleto vencendo.
// O banco (migração 077) segura só a FORMA — 47 ou 48 dígitos; a conta de
// dígito verificador mora nesta função, que tem os testes.
// =============================================================================

export type TipoCodigo = 'boleto' | 'convenio';

export type Conferencia =
  | { ok: true; linha: string; tipo: TipoCodigo }
  | { ok: false; erro: string };

/** Só os números: o boleto impresso tem pontos e espaços, o PDF tem quebra. */
export function soDigitos(texto: string): string {
  return texto.replace(/\D/g, '');
}

/** Módulo 10 (FEBRABAN): pesos 2,1,2,1… da direita, soma os DÍGITOS de cada
 *  produto (16 vale 1+6). */
function modulo10(s: string): number {
  let soma = 0;
  let peso = 2;
  for (let i = s.length - 1; i >= 0; i--) {
    const p = Number(s.charAt(i)) * peso;
    soma += Math.floor(p / 10) + (p % 10);
    peso = peso === 2 ? 1 : 2;
  }
  return (10 - (soma % 10)) % 10;
}

/** Soma do módulo 11: pesos 2 a 9 da direita, recomeçando no 2. */
function somaModulo11(s: string): number {
  let soma = 0;
  let peso = 2;
  for (let i = s.length - 1; i >= 0; i--) {
    soma += Number(s.charAt(i)) * peso;
    peso = peso === 9 ? 2 : peso + 1;
  }
  return soma;
}

/** Dígito geral do boleto de banco: 0, 10 e 11 viram 1 (nunca é zero). */
function dvGeralBoleto(s43: string): number {
  const dv = 11 - (somaModulo11(s43) % 11);
  return dv === 0 || dv === 10 || dv === 11 ? 1 : dv;
}

/** Módulo 11 do convênio: 10 e 11 viram 0. */
function modulo11Convenio(s: string): number {
  const dv = 11 - (somaModulo11(s) % 11);
  return dv >= 10 ? 0 : dv;
}

/** O convênio escolhe o módulo pelo 3º dígito: 6 e 7 são módulo 10; 8 e 9, 11. */
function moduloDoConvenio(identificador: string): ((s: string) => number) | null {
  if (identificador === '6' || identificador === '7') return modulo10;
  if (identificador === '8' || identificador === '9') return modulo11Convenio;
  return null;
}

const NUMERO_ERRADO = 'Tem um número errado no código — confira com o papel da conta.';
const ORDINAL = ['1º', '2º', '3º', '4º'];

/** Os 44 números do código de barras do BOLETO → linha digitável de 47. */
function linhaDoBoleto(barras: string): string {
  const c1 = barras.slice(0, 4) + barras.slice(19, 24);
  const c2 = barras.slice(24, 34);
  const c3 = barras.slice(34, 44);
  return c1 + modulo10(c1) + c2 + modulo10(c2) + c3 + modulo10(c3)
    + barras.charAt(4) + barras.slice(5, 19);
}

/** Linha de 47 → os 44 do código de barras (para conferir o dígito geral). */
function barrasDoBoleto(linha: string): string {
  return linha.slice(0, 4) + linha.charAt(32) + linha.slice(33, 47)
    + linha.slice(4, 9) + linha.slice(10, 20) + linha.slice(21, 31);
}

function conferirBoleto(linha: string): Conferencia {
  const campos: Array<[string, string]> = [
    [linha.slice(0, 9), linha.charAt(9)],
    [linha.slice(10, 20), linha.charAt(20)],
    [linha.slice(21, 31), linha.charAt(31)],
  ];
  for (const [i, [corpo, dv]] of campos.entries()) {
    if (String(modulo10(corpo)) !== dv) {
      return { ok: false, erro: `Tem um número errado no ${ORDINAL[i]} bloco do código — confira com o papel.` };
    }
  }
  const barras = barrasDoBoleto(linha);
  if (String(dvGeralBoleto(barras.slice(0, 4) + barras.slice(5))) !== barras.charAt(4)) {
    return { ok: false, erro: NUMERO_ERRADO };
  }
  return { ok: true, linha, tipo: 'boleto' };
}

function conferirConvenio(linha: string): Conferencia {
  const modulo = moduloDoConvenio(linha.charAt(2));
  if (!modulo) return { ok: false, erro: NUMERO_ERRADO };
  for (let i = 0; i < 4; i++) {
    const bloco = linha.slice(i * 12, i * 12 + 11);
    if (String(modulo(bloco)) !== linha.charAt(i * 12 + 11)) {
      return { ok: false, erro: `Tem um número errado no ${ORDINAL[i]} bloco do código — confira com o papel.` };
    }
  }
  const barras = [0, 1, 2, 3].map((i) => linha.slice(i * 12, i * 12 + 11)).join('');
  if (String(modulo(barras.slice(0, 3) + barras.slice(4))) !== barras.charAt(3)) {
    return { ok: false, erro: NUMERO_ERRADO };
  }
  return { ok: true, linha, tipo: 'convenio' };
}

/**
 * Confere o que foi colado ou digitado e devolve a LINHA DIGITÁVEL pronta.
 * Aceita pontos, espaços e quebras (vêm do papel e do PDF), e os 44 números
 * do código de barras.
 */
export function conferirCodigo(texto: string): Conferencia {
  const d = soDigitos(texto);
  if (!d) return { ok: false, erro: 'Cole ou digite os números que ficam em cima do código de barras.' };

  if (d.length === 44) {
    if (d.charAt(0) === '8') {
      const modulo = moduloDoConvenio(d.charAt(2));
      if (!modulo) return { ok: false, erro: NUMERO_ERRADO };
      const linha = [0, 11, 22, 33].map((i) => d.slice(i, i + 11) + modulo(d.slice(i, i + 11))).join('');
      return conferirConvenio(linha);
    }
    return conferirBoleto(linhaDoBoleto(d));
  }
  if (d.length === 47) {
    if (d.charAt(0) === '8') {
      return { ok: false, erro: 'Conta de luz, água ou imposto tem 48 números, e este tem 47 — falta um.' };
    }
    return conferirBoleto(d);
  }
  if (d.length === 48) {
    if (d.charAt(0) !== '8') {
      return { ok: false, erro: 'Boleto de banco tem 47 números, e este tem 48 — sobrou um.' };
    }
    return conferirConvenio(d);
  }
  const esperado = d.charAt(0) === '8' ? '48 (conta de luz, água ou imposto)' : '47 (boleto de banco)';
  return { ok: false, erro: `O código tem ${esperado} números, e este tem ${d.length}.` };
}

/**
 * A linha para LER na tela, agrupada como no papel. Copiar e enviar usam a
 * linha crua, só números — é o que o aplicativo do banco aceita colado.
 */
export function formatarLinha(linha: string): string {
  if (linha.length === 47) {
    return `${linha.slice(0, 5)}.${linha.slice(5, 10)} ${linha.slice(10, 15)}.${linha.slice(15, 21)} `
      + `${linha.slice(21, 26)}.${linha.slice(26, 32)} ${linha.charAt(32)} ${linha.slice(33)}`;
  }
  if (linha.length === 48) {
    return [0, 1, 2, 3].map((i) => `${linha.slice(i * 12, i * 12 + 11)}-${linha.charAt(i * 12 + 11)}`).join(' ');
  }
  return linha;
}

// --- O que o código carrega ---------------------------------------------------
//
// A linha digitável não é só um número para colar: o boleto de banco traz o
// VALOR e o VENCIMENTO dentro dele, e a conta de convênio traz o valor, o
// segmento (luz, água, telefone, imposto) e o código da empresa que cobra. É
// daqui que o cadastro pelo boleto (18/09/2026) preenche a conta sozinho.

/** Os bancos que aparecem nos boletos da loja, e os grandes. Fora da lista, a
 *  tela mostra "banco 274" — o número basta para quem confere. */
const BANCOS: Record<string, string> = {
  '001': 'Banco do Brasil', '004': 'Banco do Nordeste', '033': 'Santander', '041': 'Banrisul',
  '077': 'Inter', '104': 'Caixa', '136': 'Unicred', '208': 'BTG Pactual', '237': 'Bradesco',
  '260': 'Nubank', '290': 'PagBank', '323': 'Mercado Pago', '329': 'QI Tech', '336': 'C6 Bank',
  '341': 'Itaú', '422': 'Safra', '655': 'Banco BV', '748': 'Sicredi', '756': 'Sicoob',
};

/** Segmento do convênio (2º dígito) → categoria da conta. É SUGESTÃO: o dono
 *  confere no formulário, como tudo o que o boleto preenche. */
const SEGMENTOS: Record<string, { nome: string; categorias: string[] }> = {
  // A primeira é a sugestão; as outras são onde a loja costuma lançar a mesma
  // coisa — é contra todas que a conta repetida é procurada.
  '1': { nome: 'prefeitura', categorias: ['impostos', 'alvara'] },
  '2': { nome: 'água e saneamento', categorias: ['agua'] },
  '3': { nome: 'energia elétrica e gás', categorias: ['energia'] },
  '4': { nome: 'telefone e internet', categorias: ['telefone', 'internet'] },
  '5': { nome: 'órgão do governo', categorias: ['impostos', 'alvara'] },
  '6': { nome: 'carnê', categorias: ['outros'] },
  '7': { nome: 'multa de trânsito', categorias: ['outros'] },
};

export interface DadosDoCodigo {
  tipo: TipoCodigo;
  /** Em reais. Nulo = o código não traz valor (boleto "em aberto", ou o
   *  convênio que cobra por quantidade de moeda). */
  valor: number | null;
  /** ISO. Só o boleto de banco traz; o convênio não. */
  vencimento: string | null;
  /** "Itaú", "banco 274", "energia elétrica e gás"… — para ler na tela. */
  quem: string;
  /** Sugestão de categoria (a do segmento do convênio); nulo no boleto. */
  categoria: string | null;
  /** Todas as categorias em que esta conta pode estar lançada. */
  categorias: string[];
  /** Os 44 números do código de barras, na ordem das barras. */
  barras: string;
}

const diaISO = (d: Date) => d.toISOString().slice(0, 10);

/**
 * O fator de vencimento conta dias desde 07/10/1997 e, ao chegar a 9999 em
 * 21/02/2025, RECOMEÇOU em 1000 (22/02/2025 — norma da FEBRABAN). O mesmo
 * fator vale para duas datas, 25 anos apart; a certa é a mais perto de hoje,
 * porque boleto se paga perto de quando foi emitido.
 */
export function vencimentoDoFator(fator: number, hojeIso: string): string | null {
  if (!fator) return null;
  const hoje = new Date(`${hojeIso}T12:00:00Z`).getTime();
  const candidatos = [Date.UTC(1997, 9, 7, 12), Date.UTC(2022, 4, 29, 12)]
    .map((base) => base + fator * 86_400_000);
  const perto = candidatos.reduce((a, b) => (Math.abs(a - hoje) <= Math.abs(b - hoje) ? a : b));
  return diaISO(new Date(perto));
}

/** Tudo o que a linha digitável (já conferida) diz sobre a conta. */
export function dadosDoCodigo(linha: string, hojeIso: string): DadosDoCodigo {
  if (linha.length === 48) {
    const barras = [0, 1, 2, 3].map((i) => linha.slice(i * 12, i * 12 + 11)).join('');
    const seg = SEGMENTOS[barras.charAt(1)];
    // Identificador 6 e 8 = valor efetivo em reais; 7 e 9 = referência.
    const comValor = barras.charAt(2) === '6' || barras.charAt(2) === '8';
    const centavos = Number(barras.slice(4, 15));
    return {
      tipo: 'convenio',
      valor: comValor && centavos > 0 ? centavos / 100 : null,
      vencimento: null,
      quem: seg?.nome ?? 'convênio',
      categoria: seg?.categorias[0] ?? null,
      categorias: seg?.categorias ?? [],
      barras,
    };
  }
  const barras = linha.slice(0, 4) + linha.charAt(32) + linha.slice(33, 47)
    + linha.slice(4, 9) + linha.slice(10, 20) + linha.slice(21, 31);
  const banco = barras.slice(0, 3);
  const centavos = Number(barras.slice(9, 19));
  return {
    tipo: 'boleto',
    valor: centavos > 0 ? centavos / 100 : null,
    vencimento: vencimentoDoFator(Number(barras.slice(5, 9)), hojeIso),
    quem: BANCOS[banco] ?? `banco ${banco}`,
    categoria: null,
    categorias: [],
    barras,
  };
}

/**
 * Quantas posições do código dizem "quem cobra" iguais às de outro código —
 * zero quando não dá para comparar.
 *
 * Boleto de banco: o "campo livre" (25 números) é a conta de cobrança do
 * beneficiário naquele banco mais o número do título. Nos boletos da loja
 * medidos em 18/09/2026, o mesmo fornecedor no mesmo banco repete 18 a 24 das
 * 25 posições; fornecedores diferentes no mesmo banco (Red e Sifra, os dois no
 * Bradesco com a mesma agência) repetem 13. Convênio: o segmento e o código
 * da empresa (4 números) — a Receita e o Simples têm códigos diferentes.
 */
export function semelhancaDeCobranca(a: string, b: string): number {
  if (a.length !== b.length) return 0;
  const da = dadosDoCodigo(a, '2026-01-01').barras;
  const db = dadosDoCodigo(b, '2026-01-01').barras;
  if (a.length === 48) {
    // Segmento 6 identifica a empresa pelos 8 primeiros dígitos do CNPJ
    // (posições 16–23); os outros, por um código de 4 (16–19).
    const fim = da.charAt(1) === '6' ? 23 : 19;
    return da.charAt(1) === db.charAt(1) && da.slice(15, fim) === db.slice(15, fim) ? 25 : 0;
  }
  if (da.slice(0, 3) !== db.slice(0, 3)) return 0;
  let iguais = 0;
  for (let i = 19; i < 44; i += 1) if (da.charAt(i) === db.charAt(i)) iguais += 1;
  return iguais;
}

/** A partir de quantas posições iguais o código é do mesmo beneficiário. */
export const MESMA_COBRANCA = 18;

/**
 * O MESMO TÍTULO reemitido: 2ª via ou prorrogação troca o vencimento (e às
 * vezes o valor), mas o banco e o campo livre — conta de cobrança e nosso
 * número — continuam os mesmos. Só boleto de banco; convênio não tem isso.
 */
export function mesmoTitulo(a: string, b: string): boolean {
  if (a.length !== 47 || b.length !== 47 || a === b) return false;
  const da = dadosDoCodigo(a, '2026-01-01').barras;
  const db = dadosDoCodigo(b, '2026-01-01').barras;
  return da.slice(0, 3) === db.slice(0, 3) && da.slice(19) === db.slice(19);
}
